/**
 * Постобработка warped-изображения — фильтры для договоров.
 *
 * Виды:
 *  - 'original' — без изменений (по умолчанию)
 *  - 'enhance'  — авто-баланс: контраст + лёгкий sharpen (общий случай)
 *  - 'bw'       — чёрно-белый бинарный (adaptiveThreshold) — идеально для
 *                 печатных договоров с подписью; сильно уменьшает размер PDF
 *
 * Использует OpenCV.js через jscanify (он его уже загрузил).
 * Если cv недоступен — возвращает исходный blob.
 */

export type FilterMode = 'original' | 'enhance' | 'bw';

declare const cv: any;  // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * Применить фильтр к JPEG-blob, вернуть новый JPEG-blob.
 * При ошибке OpenCV — возвращает исходный blob (graceful fallback).
 */
export async function applyFilter(jpeg: Blob, mode: FilterMode): Promise<Blob> {
  if (mode === 'original') return jpeg;

  // Декодируем blob в canvas
  const url = URL.createObjectURL(jpeg);
  try {
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('decode fail'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return jpeg;
    ctx.drawImage(img, 0, 0);

    // Без cv — возвращаем как есть
    if (typeof cv === 'undefined' || !cv?.Mat) {
      console.debug('filters: OpenCV not loaded, skip filter');
      return jpeg;
    }

    let src: any = null;
    let dst: any = null;
    try {
      src = cv.imread(canvas);
      dst = new cv.Mat();

      if (mode === 'bw') {
        // 1. grayscale → 2. adaptiveThreshold (Gaussian, blockSize 31, C 10)
        // даёт чистую ч/б бумагу без артефактов теней.
        const gray = new cv.Mat();
        try {
          cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
          cv.adaptiveThreshold(
            gray,
            dst,
            255,
            cv.ADAPTIVE_THRESH_GAUSSIAN_C,
            cv.THRESH_BINARY,
            31, // blockSize: нечётное, ~3% от длинной стороны
            10, // C: вычитается из среднего; выше = больше белого
          );
        } finally {
          gray.delete();
        }
      } else if (mode === 'enhance') {
        // 1. light denoise → 2. unsharp mask (blur + addWeighted) → 3. CLAHE
        const blurred = new cv.Mat();
        const sharp = new cv.Mat();
        try {
          cv.GaussianBlur(src, blurred, new cv.Size(0, 0), 2.5);
          cv.addWeighted(src, 1.4, blurred, -0.4, 0, sharp);

          // CLAHE для контраста — работает в LAB на L-канале
          const lab = new cv.Mat();
          const chans = new cv.MatVector();
          try {
            cv.cvtColor(sharp, lab, cv.COLOR_RGBA2RGB);
            cv.cvtColor(lab, lab, cv.COLOR_RGB2Lab);
            cv.split(lab, chans);
            const lChan = chans.get(0);
            const clahe = new cv.CLAHE(2.5, new cv.Size(8, 8));
            try {
              clahe.apply(lChan, lChan);
            } finally {
              clahe.delete();
            }
            chans.set(0, lChan);
            cv.merge(chans, lab);
            cv.cvtColor(lab, dst, cv.COLOR_Lab2RGB);
            cv.cvtColor(dst, dst, cv.COLOR_RGB2RGBA);
          } finally {
            lab.delete();
            chans.delete();
          }
        } finally {
          blurred.delete();
          sharp.delete();
        }
      }

      // dst → canvas → blob
      cv.imshow(canvas, dst);
    } catch (err) {
      console.warn('filters: cv operation failed, fallback to original', err);
      return jpeg;
    } finally {
      src?.delete?.();
      dst?.delete?.();
    }

    return await new Promise<Blob>((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob ?? jpeg),
        'image/jpeg',
        0.92,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
